/** Planilla vinculada al dibujo + Gastos extras y servicios + totales. */
import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { COLOR_ESTADO, ICONO_TIPO } from '@/components/pizarra/LienzoPizarra';

const fQ = (n) => `Q${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

export const subtotalElemento = (el) => {
  const cant = Number(el.props?.cantidad || 1);
  const precio = Number(el.producto?.precio || 0);
  return cant * precio;
};

const NOMBRE_TIPO = { vidrio: 'Vidrio / panel', puerta: 'Puerta', bisagra: 'Bisagra', clip: 'Clip / conector', jalador: 'Jalador', perfil: 'Perfil', cerradura: 'Cerradura', union: 'Unión', otro: 'Elemento' };

export const PlanillaPizarra = ({ elementos, seleccionado, onSelect }) => {
  const filas = (elementos || []).filter((e) => e.tipo !== 'anotacion');
  const total = filas.reduce((s, e) => s + subtotalElemento(e), 0);
  const titulo = (e) => e.producto?.nombre || (e.estado === 'confirmado' || e.origen === 'manual'
    ? NOMBRE_TIPO[e.tipo] || e.tipo
    : (e.props?.descripcion || NOMBRE_TIPO[e.tipo] || e.tipo));
  return (
    <div className="border border-[#B9A5D9] rounded-[12px] overflow-hidden" data-testid="pizarra-planilla">
      <p className="text-[10px] font-black uppercase tracking-wider text-white bg-[#38205F] px-3 py-2">📋 Planilla de materiales ({filas.length})</p>
      <div className="max-h-56 overflow-y-auto divide-y divide-[#F4EFFB]">
        {filas.length === 0 && <p className="text-[11px] text-[#6B6B6B] p-3 italic">Toca un marcador del dibujo o agrega elementos.</p>}
        {filas.map((e) => (
          <button key={e.id} onClick={() => onSelect(e.id)}
            className={`w-full text-left px-3 py-2 flex items-center gap-2 ${seleccionado === e.id ? 'bg-[#B9A5D9]/40' : 'bg-white hover:bg-[#F4EFFB]'}`}
            data-testid={`planilla-fila-${e.etiqueta}`}>
            <span className="w-6 h-6 rounded-full border-2 flex items-center justify-center text-[9px] font-black shrink-0"
              style={{ borderColor: COLOR_ESTADO[e.estado], color: COLOR_ESTADO[e.estado] }}>{e.etiqueta}</span>
            <span className="flex-1 min-w-0">
              <span className="text-[11px] font-bold text-[#1F1B29] block truncate">
                {ICONO_TIPO[e.tipo]} {titulo(e)}
                {e.producto?.codigo ? ` · ${e.producto.codigo}` : ''}
              </span>
              <span className="text-[9px] text-[#6B6B6B]">
                {e.tipo === 'vidrio' || e.tipo === 'puerta'
                  ? `${e.props?.ancho_m || '?'} × ${e.props?.alto_m || '?'} m · ${e.props?.tipo_vidrio || ''} ${e.props?.espesor || ''}`
                  : `Cant: ${e.props?.cantidad || 1} ${e.producto?.unidad || 'u'}`}
                {' · '}
                {e.sin_ubicar ? '📍 Por ubicar' : e.estado === 'confirmado' ? '🟢 Confirmado' : e.estado === 'incompleto' ? '🟠 Precio/datos pendientes' : e.estado === 'error' ? '🔴 Error' : '🟣 Pendiente'}
              </span>
            </span>
            <span className="text-[11px] font-black text-[#38205F] shrink-0">
              {e.producto?.precio ? fQ(subtotalElemento(e)) : (e.tipo === 'vidrio' || e.tipo === 'puerta') ? 'vía receta' : '⏳'}
            </span>
          </button>
        ))}
      </div>
      <p className="px-3 py-2 text-[11px] font-black text-right bg-[#38205F] text-[#D5AD65]" data-testid="planilla-total">
        Materiales con precio autorizado: {fQ(total)}
      </p>
    </div>
  );
};

const CONCEPTOS = ['Mano de obra / instalación', 'Flete / transporte', 'Viáticos', 'Hotel / hospedaje', 'Alimentación / comidas', 'Combustible', 'Peajes / parqueos', 'Alquiler de equipo', 'Otros gastos'];
const UNIDADES = ['servicio', 'día', 'noche', 'persona', 'viaje', 'km', 'hora'];

export const GastosPizarra = ({ gastos, onChange }) => {
  const [tarifas, setTarifas] = useState([]);
  const [nuevo, setNuevo] = useState(null);
  useEffect(() => { api.get('/sa/pizarra/tarifas').then((r) => setTarifas(r.data.tarifas || [])).catch(() => {}); }, []);

  const tarifaDe = (concepto) => tarifas.find((t) => (t.concepto || '').toLowerCase() === concepto.toLowerCase());

  const agregar = () => {
    const t = tarifaDe(nuevo.concepto);
    const precio = Number(t?.precio || 0);
    const cantidad = Number(nuevo.cantidad || 1);
    const factor = Number(nuevo.factor || 1);
    const g = {
      id: Math.random().toString(36).slice(2, 10), concepto: nuevo.concepto,
      descripcion: nuevo.descripcion || '', cantidad, factor,
      unidad: nuevo.unidad || t?.unidad || 'servicio', precio,
      subtotal: Math.round(cantidad * factor * precio * 100) / 100,
      pendiente_precio: precio <= 0, observaciones: nuevo.observaciones || '',
    };
    onChange([...(gastos || []), g]);
    setNuevo(null);
    if (g.pendiente_precio) toast.warning('Precio pendiente de autorización — no se suma al total definitivo');
  };

  const totalOk = (gastos || []).filter((g) => !g.pendiente_precio).reduce((s, g) => s + Number(g.subtotal || 0), 0);
  return (
    <div className="border border-[#B9A5D9] rounded-[12px] overflow-hidden" data-testid="pizarra-gastos">
      <div className="flex items-center justify-between bg-[#38205F] px-3 py-2">
        <p className="text-[10px] font-black uppercase tracking-wider text-white">🧳 Gastos extras y servicios</p>
        <button onClick={() => setNuevo({ concepto: CONCEPTOS[0], cantidad: 1, factor: 1 })}
          className="text-[10px] font-black bg-white text-[#38205F] px-2 py-1 rounded-full" data-testid="gasto-agregar-btn">+ Agregar gasto o servicio</button>
      </div>
      <div className="divide-y divide-[#F4EFFB]">
        {(gastos || []).map((g) => (
          <div key={g.id} className="px-3 py-2 flex items-center gap-2" data-testid={`gasto-fila-${g.id}`}>
            <span className="flex-1 min-w-0">
              <span className="text-[11px] font-bold text-[#1F1B29] block truncate">{g.concepto}{g.descripcion ? ` — ${g.descripcion}` : ''}</span>
              <span className="text-[9px] text-[#6B6B6B]">{g.cantidad} × {g.factor} {g.unidad} × {fQ(g.precio)} {g.pendiente_precio && '· 🟠 Precio pendiente de autorización'}</span>
            </span>
            <span className="text-[11px] font-black text-[#38205F]">{g.pendiente_precio ? '⏳' : fQ(g.subtotal)}</span>
            <button onClick={() => onChange(gastos.filter((x) => x.id !== g.id))} className="text-red-400 font-black text-xs" data-testid={`gasto-eliminar-${g.id}`}>✕</button>
          </div>
        ))}
        {(gastos || []).length === 0 && !nuevo && <p className="text-[11px] text-[#6B6B6B] p-3 italic">Sin gastos. Ej: 2 instaladores × 3 días.</p>}
      </div>
      {nuevo && (
        <div className="p-3 bg-[#F4EFFB] space-y-2" data-testid="gasto-form">
          <select value={nuevo.concepto} onChange={(e) => { const t = tarifaDe(e.target.value); setNuevo({ ...nuevo, concepto: e.target.value, unidad: t?.unidad }); }}
            className="w-full h-9 px-2 text-xs rounded-md border border-[#B9A5D9] bg-white" data-testid="gasto-concepto">
            {CONCEPTOS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <Input placeholder="Descripción (ej. 2 instaladores proyecto Xela)" value={nuevo.descripcion || ''} onChange={(e) => setNuevo({ ...nuevo, descripcion: e.target.value })} className="h-9 text-xs bg-white" data-testid="gasto-descripcion" />
          <div className="grid grid-cols-3 gap-1.5">
            <Input type="number" min="1" placeholder="Cantidad" value={nuevo.cantidad} onChange={(e) => setNuevo({ ...nuevo, cantidad: e.target.value })} className="h-9 text-xs bg-white" data-testid="gasto-cantidad" />
            <Input type="number" min="1" placeholder="× días/noches/viajes" value={nuevo.factor} onChange={(e) => setNuevo({ ...nuevo, factor: e.target.value })} className="h-9 text-xs bg-white" data-testid="gasto-factor" />
            <select value={nuevo.unidad || 'servicio'} onChange={(e) => setNuevo({ ...nuevo, unidad: e.target.value })} className="h-9 px-2 text-xs rounded-md border border-[#B9A5D9] bg-white" data-testid="gasto-unidad">
              {UNIDADES.map((u) => <option key={u}>{u}</option>)}
            </select>
          </div>
          <p className="text-[10px] text-[#6B6B6B]">
            Tarifa autorizada: <b className="text-[#38205F]">{tarifaDe(nuevo.concepto)?.precio ? fQ(tarifaDe(nuevo.concepto).precio) + ` / ${tarifaDe(nuevo.concepto).unidad}` : '⚠ sin tarifa — quedará pendiente de autorización'}</b>
            {' · '}Subtotal: <b>{fQ(Number(nuevo.cantidad || 1) * Number(nuevo.factor || 1) * Number(tarifaDe(nuevo.concepto)?.precio || 0))}</b>
          </p>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="flex-1 text-xs bg-white" onClick={() => setNuevo(null)}>Cancelar</Button>
            <Button size="sm" className="flex-1 bg-[#7352B5] text-white text-xs font-bold" onClick={agregar} data-testid="gasto-guardar">Agregar</Button>
          </div>
        </div>
      )}
      <p className="px-3 py-2 text-[11px] font-black text-right bg-[#38205F] text-[#D5AD65]" data-testid="gastos-total">Total gastos autorizados: {fQ(totalOk)}</p>
    </div>
  );
};
